import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  ChangeEvent,
} from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Select, Input, Layout } from '../../../components';
import { useToast } from '../../../hooks/toast';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type { CriticalMessageDTO } from '../../../shared/interfaces/criticalMessage';
import type { ParamsProps } from '../../../shared/interfaces/parameter';
import getValidationErrors from '../../../utils/getValidationErrors';
import ProductsOptions from './../products';
import CriticalTypeOptions from './../tiposValidacao';
import { FormContainer, FormFooter } from './styles';

const Editar: React.FC = () => {
  let selectedProduct = useRef<any>();
  let selectedCriticalType = useRef<any>();
  const formRef = useRef<FormHandles | null>(null);
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<ParamsProps>();
  const { addToast } = useToast();
  const [data, setData] = useState<CriticalMessageDTO>({
    id: '',
    chave: '',
    mensagem: '',
    tiposValidacaoCritica: [],
    tiposValidacao: [],
  });

  useEffect(() => {
    if (id !== undefined) {
      findCriticalMessage(id);
    }
  }, [id]);

  function updateValue(e: ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  }

  function back() {
    if (location.pathname.match('/listar')) {
      history.push(RoutesPaths.home);
    } else {
      history.goBack();
    }
  }

  const handleSubmit = useCallback(
    async (criticalMessage: CriticalMessageDTO) => {
      criticalMessage.tiposValidacaoCritica = selectedCriticalType.current;
      criticalMessage.tiposValidacao = selectedProduct.current;
      try {
        if (
          !criticalMessage.tiposValidacao?.length &&
          !criticalMessage.tiposValidacaoCritica?.length
        ) {
          const schemaSelect1 = Yup.object().shape({
            chave: Yup.string()
              .max(200, 'No máximo 200 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            mensagem: Yup.string()
              .max(400, 'No máximo 400 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            select1: Yup.string().required('Este campo é obrigatório!'),
            select: Yup.string().required('Este campo é obrigatório!'),
          });
          await schemaSelect1.validate(criticalMessage, {
            abortEarly: false,
          });
        }
        if (!criticalMessage.tiposValidacaoCritica?.length) {
          const schemaSelect = Yup.object().shape({
            chave: Yup.string()
              .max(200, 'No máximo 200 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            mensagem: Yup.string()
              .max(400, 'No máximo 400 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            select1: Yup.string().required('Este campo é obrigatório!'),
          });
          await schemaSelect.validate(criticalMessage, {
            abortEarly: false,
          });
        }

        if (!criticalMessage.tiposValidacao?.length) {
          const schemaSelect1 = Yup.object().shape({
            chave: Yup.string()
              .max(200, 'No máximo 200 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            mensagem: Yup.string()
              .max(400, 'No máximo 400 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            select: Yup.string().required('Este campo é obrigatório!'),
          });
          await schemaSelect1.validate(criticalMessage, {
            abortEarly: false,
          });
        }

        const schema = Yup.object().shape({
          id: Yup.string().max(25, 'No máximo 25 caracteres'),
          chave: Yup.string()
            .max(200, 'No máximo 200 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
          mensagem: Yup.string()
            .max(400, 'No máximo 400 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
        });

        await schema.validate(criticalMessage, {
          abortEarly: false,
        });

        if (id !== undefined) {
          await api.put(`/criticas/${id}`, criticalMessage);
          addToast({
            type: 'success',
            title: 'Mensagem de Crítica alterada com sucesso!',
            description:
              'As informações de Mensagem de Crítica foram alteradas.',
          });
        } else {
          await api.post('/criticas', criticalMessage);
        }

        setTimeout(() => {
          history.push(RoutesPaths.listCriticalMessage);
        }, 3000);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorMessages = getValidationErrors(err);
          formRef.current?.setErrors(errorMessages);
          return;
        } else {
          console.error(err);
          addToast({
            type: 'error',
            title: 'Mensagem de Crítica já cadastrada!',
            description:
              'As informações que foram inseridas, já estão presentes no sistema.',
          });
        }
      }
    },
    [history, id, addToast],
  );

  async function findCriticalMessage(id: string) {
    const response = await api.get<CriticalMessageDTO>(`/criticas/${id}`);
    setData({
      id: response.data.id,
      chave: response.data.chave,
      mensagem: response.data.mensagem,
      tiposValidacaoCritica: response.data.tiposValidacaoCritica,
      tiposValidacao: response.data.tiposValidacao,
    });
    selectedCriticalType.current = response.data.tiposValidacaoCritica;
    selectedProduct.current = response.data.tiposValidacao;
  }

  return (
    <>
      <Layout title={'Editar Mensagem de Crítica'}>
        <FormContainer>
          <Form onSubmit={handleSubmit} ref={formRef}>
            <Input
              type="text"
              label="Chave"
              name="chave"
              value={data.chave}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e)}
            ></Input>

            <Input
              type="text"
              label="Mensagem"
              name="mensagem"
              value={data.mensagem}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e)}
            ></Input>
            <Select
              name={'select1'}
              isMulti={true}
              options={CriticalTypeOptions}
              onChange={option => {
                selectedCriticalType.current = option.map(
                  (el: any) => el.value,
                );
              }}
              placeholder="Selecione"
              label="Tipos de Validação de Critica"
              initialValues={data.tiposValidacaoCritica}
            />
            <br />
            <Select
              name={'select'}
              isMulti={true}
              options={ProductsOptions}
              onChange={option => {
                selectedProduct.current = option.map((el: any) => el.value);
              }}
              placeholder="Selecione"
              label="Tipos de validação"
              initialValues={data.tiposValidacao}
            />

            <FormFooter>
              <Button
                variant="solid"
                style={{ marginTop: '1rem' }}
                type="submit"
              >
                Salvar
              </Button>
              <Button
                variant="outline"
                color={'#C71F37'}
                style={{ marginTop: '1rem' }}
                onClick={() =>
                  setTimeout(() => {
                    back();
                  }, 300)
                }
              >
                Cancelar
              </Button>
            </FormFooter>
          </Form>
        </FormContainer>
      </Layout>
    </>
  );
};

export default Editar;
